// Notifications state

import { Request } from "@asanrom/request-browser";
import { Timeouts } from "@/utils/timeout";
import { AppEvents } from "./app-events";
import { AuthController } from "./auth";
import { ApiNotifications } from "@/api/api-group-notifications";
import { type Notification } from "@/api/definitions";

export type NotificationItem = {
    id: string;
    type: string;
    info: Record<string, unknown> | null;
    date: number;
    read: boolean;
};

const LOAD_LIST_ID = "notifications-controller-load-list";
const LOAD_PREVIEW_ID = "notifications-controller-load-preview";
const LOAD_UNREAD_ID = "notifications-controller-load-unread";
const MARK_ALL_ID = "notifications-controller-mark-all";
const DELETE_ALL_ID = "notifications-controller-delete-all";
const PREVIEW_PAGE_SIZE = 10;
const NOTIFICATIONS_PAGE_SIZE = 7;

function markOneRequestId(id: string): string {
    return `notifications-controller-mark-one:${id}`;
}

function deleteOneRequestId(id: string): string {
    return `notifications-controller-delete-one:${id}`;
}

function mapNotification(n: Notification): NotificationItem {
    const rawInfo = n.data?.info;
    const info = rawInfo && typeof rawInfo === "object" && !Array.isArray(rawInfo) ? (rawInfo as Record<string, unknown>) : null;
    return {
        id: n.id || "",
        type: n.data?.type || "unknown",
        info,
        date: n.date || 0,
        read: !!n.read,
    };
}

export class NotificationsController {
    public static Initialized = false;
    public static notifications: NotificationItem[] = [];
    public static previewNotifications: NotificationItem[] = [];
    public static unreadCount = 0;
    public static total = 0;
    public static page = 1;
    public static totalPages = 1;
    public static loading = false;

    public static Initialize() {
        if (NotificationsController.Initialized) {
            return;
        }
        NotificationsController.Initialized = true;

        AppEvents.AddEventListener("auth-status-changed", NotificationsController.onAuthStatusChanged);
        AppEvents.AddEventListener("notification", NotificationsController.onRealtimeNotification);

        NotificationsController.onAuthStatusChanged();
    }

    public static GetNotifications(): NotificationItem[] {
        return NotificationsController.notifications.slice();
    }

    public static GetPreviewNotifications(): NotificationItem[] {
        return NotificationsController.previewNotifications.slice();
    }

    public static HasUnread(): boolean {
        return NotificationsController.unreadCount > 0;
    }

    public static emitChanged(): void {
        AppEvents.Emit("notifications-changed", NotificationsController.HasUnread());
    }

    public static SetUnreadCount(count: number): void {
        const normalized = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
        const previous = NotificationsController.unreadCount;
        if (previous === normalized) {
            return;
        }
        NotificationsController.unreadCount = normalized;
        NotificationsController.emitChanged();
        if (normalized > previous && AuthController.isAuthenticated()) {
            NotificationsController.LoadPreview(PREVIEW_PAGE_SIZE, undefined, true);
            NotificationsController.Load(NotificationsController.page, NOTIFICATIONS_PAGE_SIZE, undefined, true);
        }
    }

    private static onAuthStatusChanged = () => {
        if (!AuthController.isAuthenticated()) {
            Request.Abort(LOAD_UNREAD_ID);
            NotificationsController.unreadCount = 0;
            NotificationsController.notifications = [];
            NotificationsController.previewNotifications = [];
            NotificationsController.total = 0;
            NotificationsController.emitChanged();
            AppEvents.Emit("notifications-preview-changed");
            return;
        }

        NotificationsController.LoadUnreadCount();
    };

    private static onRealtimeNotification = () => {
        if (!AuthController.isAuthenticated()) {
            return;
        }
        NotificationsController.LoadUnreadCount();
    };

    public static LoadUnreadCount(onDone?: () => void) {
        Timeouts.Abort(LOAD_UNREAD_ID);
        Request.Abort(LOAD_UNREAD_ID);

        if (!AuthController.isAuthenticated()) {
            NotificationsController.SetUnreadCount(0);
            NotificationsController.notifications = [];
            NotificationsController.previewNotifications = [];
            NotificationsController.total = 0;
            AppEvents.Emit("notifications-preview-changed");
            if (onDone) {
                onDone();
            }
            return;
        }

        Request.Pending(LOAD_UNREAD_ID, ApiNotifications.CountUnreadNotifications())
            .onSuccess((result) => {
                NotificationsController.SetUnreadCount(result.unreadCount || 0);
                if (onDone) {
                    onDone();
                }
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        NotificationsController.SetUnreadCount(0);
                        AppEvents.Emit("unauthorized");
                        if (onDone) {
                            onDone();
                        }
                    },
                    temporalError: () => {
                        Timeouts.Set(LOAD_UNREAD_ID, 1500, () => {
                            NotificationsController.LoadUnreadCount(onDone);
                        });
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error(err);
                Timeouts.Set(LOAD_UNREAD_ID, 1500, () => {
                    NotificationsController.LoadUnreadCount(onDone);
                });
            });
    }

    private static hintUnreadFromItems(items: NotificationItem[]): void {
        const unreadInView = items.reduce((n, item) => n + (item.read ? 0 : 1), 0);
        if (unreadInView > 0 && NotificationsController.unreadCount === 0) {
            NotificationsController.unreadCount = unreadInView;
            NotificationsController.emitChanged();
        }
    }

    public static Load(page = 1, pageSize = NOTIFICATIONS_PAGE_SIZE, onDone?: () => void, silent = false) {
        Timeouts.Abort(LOAD_LIST_ID);
        Request.Abort(LOAD_LIST_ID);

        if (!AuthController.isAuthenticated()) {
            NotificationsController.notifications = [];
            NotificationsController.total = 0;
            NotificationsController.loading = false;
            NotificationsController.emitChanged();
            if (onDone) {
                onDone();
            }
            return;
        }

        if (!silent) {
            NotificationsController.loading = true;
        }

        Request.Pending(LOAD_LIST_ID, ApiNotifications.ListNotifications({ page, pageSize }))
            .onSuccess((result) => {
                NotificationsController.notifications = (result.notifications || []).map(mapNotification);
                NotificationsController.total = result.total || 0;
                NotificationsController.page = result.page || 1;
                NotificationsController.totalPages = result.totalPages || 1;
                NotificationsController.loading = false;
                NotificationsController.hintUnreadFromItems(NotificationsController.notifications);
                AppEvents.Emit("notifications-list-changed");

                const finish = () => {
                    if (onDone) {
                        onDone();
                    }
                };

                if (!silent) {
                    NotificationsController.LoadUnreadCount(finish);
                } else {
                    finish();
                }
            })
            .onRequestError((err, handleErr) => {
                NotificationsController.loading = false;
                handleErr(err, {
                    unauthorized: () => {
                        AppEvents.Emit("unauthorized");
                    },
                    temporalError: () => {
                        Timeouts.Set(LOAD_LIST_ID, 1500, () => {
                            NotificationsController.Load(page, pageSize, onDone, silent);
                        });
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error(err);
                NotificationsController.loading = false;
                Timeouts.Set(LOAD_LIST_ID, 1500, () => {
                    NotificationsController.Load(page, pageSize, onDone, silent);
                });
            });
    }

    public static LoadPreview(limit = PREVIEW_PAGE_SIZE, onDone?: () => void, silent = false) {
        Timeouts.Abort(LOAD_PREVIEW_ID);
        Request.Abort(LOAD_PREVIEW_ID);

        if (!AuthController.isAuthenticated()) {
            NotificationsController.previewNotifications = [];
            AppEvents.Emit("notifications-preview-changed");
            if (onDone) {
                onDone();
            }
            return;
        }

        Request.Pending(LOAD_PREVIEW_ID, ApiNotifications.ListNotifications({ page: 1, pageSize: limit }))
            .onSuccess((result) => {
                NotificationsController.previewNotifications = (result.notifications || []).map(mapNotification);
                NotificationsController.hintUnreadFromItems(NotificationsController.previewNotifications);
                AppEvents.Emit("notifications-preview-changed");

                const finish = () => {
                    if (onDone) {
                        onDone();
                    }
                };

                if (!silent) {
                    NotificationsController.LoadUnreadCount(finish);
                } else {
                    finish();
                }
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        AppEvents.Emit("unauthorized");
                        if (onDone) {
                            onDone();
                        }
                    },
                    temporalError: () => {
                        Timeouts.Set(LOAD_PREVIEW_ID, 1500, () => {
                            NotificationsController.LoadPreview(limit, onDone, silent);
                        });
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error(err);
                Timeouts.Set(LOAD_PREVIEW_ID, 1500, () => {
                    NotificationsController.LoadPreview(limit, onDone, silent);
                });
            });
    }

    public static MarkAllRead(onDone?: (ok: boolean) => void) {
        if (!AuthController.isAuthenticated()) {
            if (onDone) {
                onDone(false);
            }
            return;
        }

        Timeouts.Abort(MARK_ALL_ID);
        Request.Abort(MARK_ALL_ID);

        Request.Pending(MARK_ALL_ID, ApiNotifications.MarkAllNotificationsAsRead())
            .onSuccess(() => {
                for (const notification of NotificationsController.notifications) {
                    notification.read = true;
                }
                for (const notification of NotificationsController.previewNotifications) {
                    notification.read = true;
                }
                NotificationsController.SetUnreadCount(0);
                AppEvents.Emit("notifications-preview-changed");
                if (onDone) {
                    onDone(true);
                }
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        AppEvents.Emit("unauthorized");
                        if (onDone) {
                            onDone(false);
                        }
                    },
                    temporalError: () => {
                        if (onDone) {
                            onDone(false);
                        }
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error(err);
                if (onDone) {
                    onDone(false);
                }
            });
    }

    public static MarkRead(id: string, onDone?: (ok: boolean) => void) {
        if (!AuthController.isAuthenticated()) {
            if (onDone) {
                onDone(false);
            }
            return;
        }

        const requestId = markOneRequestId(id);
        Request.Pending(requestId, ApiNotifications.MarkNotificationAsRead(id))
            .onSuccess(() => {
                const item = NotificationsController.notifications.find((n) => n.id === id);
                const previewItem = NotificationsController.previewNotifications.find((n) => n.id === id);
                let wasUnread = false;

                if (item && !item.read) {
                    item.read = true;
                    wasUnread = true;
                }
                if (previewItem && !previewItem.read) {
                    previewItem.read = true;
                    wasUnread = true;
                }

                if (wasUnread && NotificationsController.unreadCount > 0) {
                    NotificationsController.SetUnreadCount(NotificationsController.unreadCount - 1);
                }

                NotificationsController.emitChanged();
                AppEvents.Emit("notifications-preview-changed");
                if (onDone) {
                    onDone(true);
                }
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        AppEvents.Emit("unauthorized");
                        if (onDone) {
                            onDone(false);
                        }
                    },
                    notFound: () => {
                        if (onDone) {
                            onDone(false);
                        }
                    },
                    notFoundNotificationNotFound: () => {
                        if (onDone) {
                            onDone(false);
                        }
                    },
                    temporalError: () => {
                        if (onDone) {
                            onDone(false);
                        }
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error(err);
                if (onDone) {
                    onDone(false);
                }
            });
    }

    public static Delete(id: string, onDone?: (ok: boolean) => void) {
        if (!AuthController.isAuthenticated()) {
            if (onDone) {
                onDone(false);
            }
            return;
        }

        const requestId = deleteOneRequestId(id);
        Request.Pending(requestId, ApiNotifications.DeleteNotification(id))
            .onSuccess(() => {
                const idx = NotificationsController.notifications.findIndex((n) => n.id === id);
                if (idx >= 0) {
                    const wasUnread = !NotificationsController.notifications[idx].read;
                    NotificationsController.notifications.splice(idx, 1);
                    if (wasUnread && NotificationsController.unreadCount > 0) {
                        NotificationsController.SetUnreadCount(NotificationsController.unreadCount - 1);
                    }
                    if (NotificationsController.total > 0) {
                        NotificationsController.total--;
                    }
                }
                const previewIdx = NotificationsController.previewNotifications.findIndex((n) => n.id === id);
                if (previewIdx >= 0) {
                    NotificationsController.previewNotifications.splice(previewIdx, 1);
                }
                NotificationsController.emitChanged();
                AppEvents.Emit("notifications-list-changed");
                AppEvents.Emit("notifications-preview-changed");
                if (onDone) {
                    onDone(true);
                }
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        AppEvents.Emit("unauthorized");
                        if (onDone) {
                            onDone(false);
                        }
                    },
                    notFound: () => {
                        if (onDone) {
                            onDone(false);
                        }
                    },
                    notFoundNotificationNotFound: () => {
                        if (onDone) {
                            onDone(false);
                        }
                    },
                    temporalError: () => {
                        if (onDone) {
                            onDone(false);
                        }
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error(err);
                if (onDone) {
                    onDone(false);
                }
            });
    }

    public static DeleteAll(onDone?: (ok: boolean) => void) {
        if (!AuthController.isAuthenticated()) {
            if (onDone) {
                onDone(false);
            }
            return;
        }

        Timeouts.Abort(DELETE_ALL_ID);
        Request.Abort(DELETE_ALL_ID);

        Request.Pending(DELETE_ALL_ID, ApiNotifications.DeleteAllNotifications())
            .onSuccess(() => {
                NotificationsController.notifications = [];
                NotificationsController.previewNotifications = [];
                NotificationsController.total = 0;
                NotificationsController.SetUnreadCount(0);
                AppEvents.Emit("notifications-list-changed");
                AppEvents.Emit("notifications-preview-changed");
                if (onDone) {
                    onDone(true);
                }
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        AppEvents.Emit("unauthorized");
                        if (onDone) {
                            onDone(false);
                        }
                    },
                    temporalError: () => {
                        if (onDone) {
                            onDone(false);
                        }
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error(err);
                if (onDone) {
                    onDone(false);
                }
            });
    }
}
